import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesDown, faAnglesUp } from '@fortawesome/free-solid-svg-icons';
import { SearchBar } from '../components/SearchBar';
import { OfferList, SummaryLine } from '../components/OfferList';
import { PropertyGroupList } from '../components/PropertyGroupList';
import { OfferFilterBar } from '../components/OfferFilterBar';
import { QuoteRail } from '../components/QuoteRail';
import { ViewToggle } from '../components/ViewToggle';
import { AvailabilityView } from '../components/AvailabilityView';
import { AvailabilityToolbar } from '../components/AvailabilityToolbar';
import { SearchPageSkeleton } from '../components/Skeletons';
import { useExpandableGroups } from '../util/useExpandableGroups';
import { useDateWindow } from '../availability/useDateWindow';
import { groupOffersByRoomType, applyOfferFilters, DEFAULT_OFFER_FILTERS } from '../offers/offerView';
import { toAvailabilityContext } from '../availability/availabilityContexts';
import { buildSearchOptions, resolvePropertiesForSelection } from '../markets/marketResolver';
import { marketGroupKey } from '../util/groupKeys';
import { useMarketSearch } from '../markets/useMarketSearch';
import { useLazySearchOffersQuery } from '../services/offersApi';
import { useGetCmsConfigQuery } from '../services/cmsConfigApi';
import { useAppDispatch } from '../store/hooks';
import { addLineItem } from '../quote/quoteSlice';
import { toOffersRequest } from '../schemas';
// The single-search result names the property; the quote line needs a
// {id, name} ref for that property.
const propertyRefFrom = (result) => {
    const property = result?.property;
    if (!property) {
        return undefined;
    }
    return { id: property.id, name: property.name ?? property.code ?? property.id };
};
const statusFor = (result) => {
    if (result.isFetching) {
        return 'loading';
    }
    if (result.isError) {
        return 'error';
    }
    if (result.isUninitialized) {
        return 'idle';
    }
    return 'success';
};
// The one authenticated view: search on top, results below. Results render as
// either the grouped offer list or the availability calendar (the ViewToggle
// switches between them over the same search). A property search hits a single
// property; a city/region search fans out and groups by property. The route
// owns the triggers; resolution, grouping, and rendering are delegated.
export const Search = () => {
    const { data: config, isLoading: configLoading, isError: configError } = useGetCmsConfigQuery();
    const [single, singleResult] = useLazySearchOffersQuery();
    const market = useMarketSearch();
    const dispatch = useAppDispatch();
    const [mode, setMode] = useState('property');
    const [view, setView] = useState('offers');
    const [lastRequest, setLastRequest] = useState();
    const [filters, setFilters] = useState(DEFAULT_OFFER_FILTERS);
    const options = config ? buildSearchOptions(config) : [];
    const addLine = (draft, quantity) => dispatch(addLineItem(draft, quantity));
    const handleSearch = (request) => {
        setLastRequest(request);
        if (request.selection.type === 'property') {
            setMode('property');
            single(toOffersRequest(request.selection.id, request));
            return;
        }
        setMode('market');
        const properties = config
            ? resolvePropertiesForSelection(config, request.selection)
            : [];
        void market.run(properties, request);
    };
    const busy = singleResult.isFetching || market.state.status === 'loading';
    const allGroups = singleResult.data
        ? groupOffersByRoomType(singleResult.data.offers)
        : [];
    const groups = applyOfferFilters(allGroups, filters);
    const marketResult = filterMarketResult(market.state.result, filters);
    const searchedProperty = propertyRefFrom(singleResult.data);
    // Filters run before the filter bar shows, so gate the bar on the raw result
    // — otherwise hiding everything would also hide the controls to unhide it.
    const hasResults = mode === 'property'
        ? allGroups.length > 0
        : (market.state.result?.groups.length ?? 0) > 0;
    const expansion = useExpandableGroups(groupCardIds(mode, groups, marketResult));
    // The availability index has its own expansion, governing the rate-plan rows
    // under each room type. It starts collapsed so the calendar opens as a compact
    // house-count overview, and its keys are namespaced by property (room-type ids
    // repeat across a market's properties).
    const calendarExpansion = useExpandableGroups(calendarGroupKeys(mode, searchedProperty, groups, marketResult), { defaultExpanded: false });
    // The availability calendar reuses the offers already loaded for this search:
    // one context per property, carrying its rate-plan rows to request.
    const contexts = availabilityContexts(mode, searchedProperty, groups, marketResult);
    const range = lastRequest
        ? { start: lastRequest.arrival, end: lastRequest.departure }
        : undefined;
    const dateWindow = useDateWindow(range);
    return (_jsxs("main", { className: "mx-auto max-w-7xl px-4 py-6", children: [_jsx("h1", { className: "mb-4 text-2xl font-bold text-midnight", children: "Sales Desk" }), configLoading ? (_jsx(SearchPageSkeleton, {})) : configError ? (_jsx(ConfigError, {})) : (_jsxs(_Fragment, { children: [_jsx(SearchBar, { options: options, onSearch: handleSearch, config: config, busy: busy }), _jsxs("div", { className: "mb-4 mt-6 flex flex-wrap items-center justify-between gap-3", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-x-6 gap-y-2", children: [_jsx(ViewToggle, { view: view, onChange: setView }), view === 'offers' && hasResults && (_jsx(OfferFilterBar, { filters: filters, onChange: setFilters })), view === 'offers' && hasResults && (_jsx(ExpandCollapseAll, { expansion: expansion })), view === 'calendar' && contexts.length > 0 && (_jsx(ExpandCollapseAll, { expansion: calendarExpansion }))] }), _jsxs("div", { className: "flex flex-wrap items-center gap-x-4 gap-y-2", children: [view === 'calendar' && contexts.length > 0 && (_jsx(AvailabilityToolbar, { controls: dateWindow })), view === 'offers' &&
                                        mode === 'property' &&
                                        groups.length > 0 && _jsx(SummaryLine, { groups: groups })] })] }), _jsxs("div", { className: "flex flex-col gap-6 lg:flex-row lg:items-start", children: [_jsx("div", { className: "min-w-0 flex-1", children: view === 'offers' ? (mode === 'property' ? (_jsx(OfferList, { status: statusFor(singleResult), groups: groups, property: searchedProperty, onAddLine: addLine, expansion: expansion })) : (_jsx(PropertyGroupList, { status: market.state.status, result: marketResult, onAddLine: addLine, expansion: expansion }))) : (_jsx(AvailabilityView, { contexts: contexts, range: range, dateWindow: dateWindow.dateWindow, adults: lastRequest?.adults ?? 1, hideUnavailable: filters.hideUnavailable, expansion: calendarExpansion })) }), hasResults && (
                            // Pin below the sticky TopNav (~56px) with a little breathing room, so
                            // the rail never slides under the bar as the results scroll.
                            _jsx("div", { className: "w-full lg:sticky lg:top-20 lg:w-80", children: _jsx(QuoteRail, {}) }))] })] }))] }));
};
// The shared expand/collapse-all control. Sits next to the filters and drives
// whichever expansion it is handed — the offer-list cards or the availability
// index's rate-plan rows.
const ExpandCollapseAll = ({ expansion }) => (_jsxs("button", { type: "button", className: "btn btn-ghost btn-sm gap-2 text-light-midnight", onClick: expansion.allExpanded ? expansion.collapseAll : expansion.expandAll, children: [_jsx(FontAwesomeIcon, { icon: expansion.allExpanded ? faAnglesUp : faAnglesDown }), expansion.allExpanded ? 'Collapse all' : 'Expand all'] }));
// The whole tool depends on the CMS config for its search options; if it fails
// to load there is nothing to search, so surface it rather than a dead search
// bar.
const ConfigError = () => (_jsx("div", { className: "rounded-2xl border border-error/40 bg-error/5 px-4 py-6 text-sm text-error", children: "Couldn\u2019t load the Sales Desk configuration. Please refresh to try again." }));
// Derives the per-property calendar contexts from whichever search ran.
const availabilityContexts = (mode, searchedProperty, groups, marketResult) => {
    if (mode === 'property') {
        if (!searchedProperty || groups.length === 0) {
            return [];
        }
        return [toAvailabilityContext(searchedProperty, groups)];
    }
    return (marketResult?.groups ?? []).map(group => toAvailabilityContext(group.property, group.roomTypes));
};
// Applies the offer filters to a fan-out result: filter each property's room
// types and drop properties left with none, preserving failed-property info.
const filterMarketResult = (result, filters) => {
    if (!result) {
        return undefined;
    }
    const groups = result.groups
        .map(group => ({
        ...group,
        roomTypes: applyOfferFilters(group.roomTypes, filters)
    }))
        .filter(group => group.roomTypes.length > 0);
    return { ...result, groups };
};
// The expansion keys for whichever result is showing: room type id in property
// mode, namespaced by property id in market mode (ids repeat across properties).
const groupCardIds = (mode, groups, marketResult) => {
    if (mode === 'property') {
        return groups.map(group => group.roomTypeId);
    }
    return (marketResult?.groups ?? []).flatMap(group => group.roomTypes.map(roomType => marketGroupKey(group.property.id, roomType.roomTypeId)));
};
// The availability index namespaces every room type by property (in both single
// and market searches), matching the key each PropertyAvailabilityCard builds —
// so the shared expand/collapse-all state lines up with what each card toggles.
const calendarGroupKeys = (mode, searchedProperty, groups, marketResult) => {
    if (mode === 'property') {
        if (!searchedProperty) {
            return [];
        }
        return groups.map(group => marketGroupKey(searchedProperty.id, group.roomTypeId));
    }
    return (marketResult?.groups ?? []).flatMap(group => group.roomTypes.map(roomType => marketGroupKey(group.property.id, roomType.roomTypeId)));
};
